import React, { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./AppTooltip.css";

function elementIsTruncated(node) {
  if (!node || !(node instanceof HTMLElement)) return false;
  return (
    node.scrollWidth > node.clientWidth + 1 ||
    node.scrollHeight > node.clientHeight + 1
  );
}

function isTruncatedTrigger(root) {
  if (!root) return false;
  if (elementIsTruncated(root)) return true;
  const child = root.firstElementChild;
  if (child && elementIsTruncated(child)) return true;
  // Walk one level deeper for nested ellipsis spans.
  if (child?.firstElementChild && elementIsTruncated(child.firstElementChild)) {
    return true;
  }
  return false;
}

/**
 * Custom hover/focus tooltip matching the admin dashboard tip style.
 * Uses a fixed portal so table overflow does not clip the bubble.
 *
 * For truncated text tips, pass wrap (onlyWhenTruncated defaults true with wrap)
 * so the tip only appears when content actually overflows.
 */
export default function AppTooltip({
  label,
  children,
  placement = "top",
  wrap = false,
  plain = false,
  className = "",
  disabled = false,
  onlyWhenTruncated = wrap,
}) {
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const tipId = useId();

  const text =
    label == null || label === false ? "" : String(label).trim();
  const active = !disabled && Boolean(text);

  const tryOpen = useCallback(() => {
    if (!active) return;
    if (onlyWhenTruncated && !isTruncatedTrigger(triggerRef.current)) {
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [active, onlyWhenTruncated]);

  const close = useCallback(() => setOpen(false), []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;

    const rect = trigger.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const gap = 8;
    const pad = 8;
    let top = 0;
    let left = 0;

    if (placement === "bottom") {
      top = rect.bottom + gap;
      left = rect.left + rect.width / 2 - tipRect.width / 2;
    } else if (placement === "left") {
      top = rect.top + rect.height / 2 - tipRect.height / 2;
      left = rect.left - tipRect.width - gap;
    } else if (placement === "right") {
      top = rect.top + rect.height / 2 - tipRect.height / 2;
      left = rect.right + gap;
    } else {
      top = rect.top - tipRect.height - gap;
      left = rect.left + rect.width / 2 - tipRect.width / 2;
    }

    left = Math.min(
      Math.max(pad, left),
      window.innerWidth - tipRect.width - pad,
    );
    top = Math.min(
      Math.max(pad, top),
      window.innerHeight - tipRect.height - pad,
    );

    setCoords({ top, left });
  }, [placement]);

  useLayoutEffect(() => {
    if (!open || !active) return undefined;
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, active, updatePosition, text]);

  if (!active) {
    return children;
  }

  return (
    <span
      ref={triggerRef}
      className={`app-tip${className ? ` ${className}` : ""}`}
      onMouseEnter={tryOpen}
      onMouseLeave={close}
      onFocus={tryOpen}
      onBlur={close}
      aria-describedby={open ? tipId : undefined}
    >
      {children}
      {open
        ? createPortal(
            <span
              ref={tipRef}
              id={tipId}
              role="tooltip"
              className={`app-tip__bubble${wrap ? " app-tip__bubble--wrap" : ""}${
                plain ? " app-tip__bubble--plain" : ""
              }`}
              style={
                coords
                  ? { top: coords.top, left: coords.left }
                  : { top: -9999, left: -9999, visibility: "hidden" }
              }
            >
              <span className="app-tip__bubble-text">{text}</span>
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
